import React from 'react';
import { Link } from 'react-router-dom';
import { FaPhone, FaEnvelope, FaGlobe, FaAngleUp, FaFacebook, FaInstagram, FaLinkedin, FaYoutube } from 'react-icons/fa';
import { motion } from 'framer-motion';
// Import logo from assets
// import logo from '../../assets/images/home/logo/logo-wide.png';
// Logo will be referenced directly from public folder

const Footer = () => {
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <footer className="relative bg-gradient-to-b from-gray-900 to-gray-950 text-white overflow-hidden">
      {/* Decorative gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-r from-indigo-500/10 to-purple-500/10" />
      
      {/* Main footer content */}
      <div className="relative container mx-auto py-16 px-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Logo and Contact Info */}
          <div className="flex flex-col">
            <div className="mb-8 transform hover:scale-105 transition-transform duration-300">
              <img 
                src="/images/home/logo/logo-wide.png" 
                alt="STEM Labs" 
                className="w-64"
              />
            </div>
            <p className="text-gray-400 mb-6 leading-relaxed">
              No 137/17, Old Kesbewa Rd,<br />
              Gangodawila, Nugegoda, Sri Lanka.
            </p>
            <div className="space-y-4">
              <motion.div 
                className="flex items-center text-gray-400 group"
                whileHover={{ x: 5 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                <div className="mr-3 text-indigo-400 group-hover:text-indigo-300 transition-colors">
                  <FaPhone />
                </div>
                <span className="group-hover:text-white transition-colors">
                  011 71 800 10 / 071-7349697 / 077 908 3607
                </span>
              </motion.div>
              <motion.div 
                className="flex items-center text-gray-400 group"
                whileHover={{ x: 5 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                <div className="mr-3 text-indigo-400 group-hover:text-indigo-300 transition-colors">
                  <FaEnvelope />
                </div>
                <div className="space-y-1">
                  <a href="mailto:info@stemlabs.edu.lk" className="block hover:text-white transition-colors">
                    info@stemlabs.edu.lk
                  </a>
                  <a href="mailto:stemlabs.edu@gmail.com" className="block hover:text-white transition-colors">
                    stemlabs.edu@gmail.com
                  </a>
                </div>
              </motion.div>
              <motion.div 
                className="flex items-center text-gray-400 group"
                whileHover={{ x: 5 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                <div className="mr-3 text-indigo-400 group-hover:text-indigo-300 transition-colors">
                  <FaGlobe />
                </div>
                <a href="https://stemlabs.edu.lk" className="hover:text-white transition-colors">
                  www.stemlabs.edu.lk
                </a>
              </motion.div>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-xl font-bold mb-8 relative inline-block">
              Quick Links
              <span className="absolute bottom-0 left-0 w-full h-0.5 bg-gradient-to-r from-indigo-500 to-purple-500"></span>
            </h3>
            <ul className="space-y-4">
              {[
                { to: '/about', text: 'About Us' },
                { to: '/courses', text: 'Our Courses' },
                { to: '/workshops', text: 'Workshops' },
                { to: '/events', text: 'Events' },
                { to: '/gallery', text: 'Gallery' },
                { to: '/news', text: 'News' },
                { to: '/contact', text: 'Contact' }
              ].map((link, index) => (
                <motion.li 
                  key={index}
                  whileHover={{ x: 5 }}
                  transition={{ type: "spring", stiffness: 300 }}
                >
                  <Link 
                    to={link.to} 
                    className="text-gray-400 hover:text-white transition-colors flex items-center group"
                  >
                    <span className="w-1.5 h-1.5 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full mr-3 group-hover:scale-150 transition-transform"></span>
                    {link.text}
                  </Link>
                </motion.li>
              ))}
            </ul>
          </div>

          {/* Opening Hours */}
          <div>
            <h3 className="text-xl font-bold mb-8 relative inline-block">
              Opening Hours
              <span className="absolute bottom-0 left-0 w-full h-0.5 bg-gradient-to-r from-indigo-500 to-purple-500"></span>
            </h3>
            <ul className="space-y-4">
              {[
                { days: 'Mon - Tues', hours: '8.00 am - 10.00 pm' },
                { days: 'Wednes - Thurs', hours: '9.00 am - 10.00 pm' },
                { days: 'Fri', hours: '9.00 pm - 8.00 pm' },
                { days: 'Sat - Sun', hours: '12.00 pm - 8.00 pm' }
              ].map((schedule, index) => (
                <motion.li 
                  key={index}
                  className="flex justify-between items-center p-2 rounded-lg hover:bg-white/5 transition-colors"
                  whileHover={{ x: 5 }}
                  transition={{ type: "spring", stiffness: 300 }}
                >
                  <span className="text-gray-400">{schedule.days}</span>
                  <span className="text-gray-300 font-medium">{schedule.hours}</span>
                </motion.li>
              ))}
            </ul>
          </div>

          {/* Social Media & Newsletter */}
          <div>
            <h3 className="text-xl font-bold mb-8 relative inline-block">
              Connect With Us
              <span className="absolute bottom-0 left-0 w-full h-0.5 bg-gradient-to-r from-indigo-500 to-purple-500"></span>
            </h3>
            <div className="space-y-6">
              <div className="flex space-x-4">
                {[
                  { icon: FaFacebook, href: 'https://www.facebook.com/STEMLabSL/' },
                  { icon: FaInstagram, href: 'https://www.instagram.com/_stemlabs.edu.lk_/' },
                  { icon: FaLinkedin, href: '#' },
                  { icon: FaYoutube, href: '#' }
                ].map((social, index) => (
                  <motion.a
                    key={index}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-gradient-to-r from-indigo-500 to-purple-500 transition-all duration-300"
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <social.icon />
                  </motion.a>
                ))}
              </div>
              <div className="bg-white/5 rounded-lg p-4">
                <h4 className="text-sm font-medium mb-3">Subscribe to our newsletter</h4>
                <form className="space-y-3">
                  <input
                    type="email"
                    placeholder="Enter your email"
                    className="w-full px-4 py-2 rounded-lg bg-white/10 border border-white/20 text-white placeholder-gray-400 focus:outline-none focus:border-indigo-500 transition-colors"
                  />
                  <button
                    type="submit"
                    className="w-full px-4 py-2 rounded-lg bg-gradient-to-r from-indigo-500 to-purple-500 text-white font-medium hover:shadow-lg hover:shadow-indigo-500/25 transition-all duration-300"
                  >
                    Subscribe
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Copyright Bar */}
      <div className="relative bg-black/50 backdrop-blur-sm py-6">
        <div className="container mx-auto px-4 flex justify-between items-center">
          <p className="text-gray-500 text-sm">
            Copyright ©2024 Institute of STEM education (Pvt) Ltd. All Rights Reserved
          </p>
          <motion.button 
            onClick={scrollToTop}
            className="w-10 h-10 rounded-full bg-gradient-to-r from-indigo-500 to-purple-500 flex items-center justify-center text-white hover:shadow-lg hover:shadow-indigo-500/25 transition-all duration-300"
            whileHover={{ y: -5 }}
            whileTap={{ scale: 0.95 }}
            aria-label="Scroll to top"
          >
            <FaAngleUp />
          </motion.button>
        </div>
      </div>
    </footer>
  );
};

export default Footer; 